// src/pages/admin/ReturnsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminReturnsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { RotateCcw, Check, X, Truck } from 'lucide-react'

const STATUS_COLORS = {
  requested:        'bg-amber-100 text-amber-700',
  approved:         'bg-blue-100 text-blue-700',
  pickup_scheduled: 'bg-indigo-100 text-indigo-700',
  picked_up:        'bg-purple-100 text-purple-700',
  inspected:        'bg-cyan-100 text-cyan-700',
  refund_initiated: 'bg-green-100 text-green-700',
  completed:        'bg-green-100 text-green-700',
  rejected:         'bg-red-100 text-red-700',
}

export default function ReturnsPage() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-returns', statusFilter],
    queryFn: () => adminReturnsApi.list({ status: statusFilter || undefined }).then(r => r.data),
  })

  const approveMut = useMutation({
    mutationFn: (id) => adminReturnsApi.approve(id, {}),
    onSuccess: () => { toast.success('Return approved'); qc.invalidateQueries({ queryKey: ['admin-returns'] }) },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) => adminReturnsApi.reject(id, { reason }),
    onSuccess: () => { toast.success('Return rejected'); qc.invalidateQueries({ queryKey: ['admin-returns'] }) },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  })

  const transitionMut = useMutation({
    mutationFn: ({ id, status }) => adminReturnsApi.transition(id, { status }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['admin-returns'] }) },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  })

  const returns = data?.data?.data ?? data?.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Returns & Exchanges</h1>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="requested">Requested</option>
          <option value="approved">Approved</option>
          <option value="pickup_scheduled">Pickup Scheduled</option>
          <option value="picked_up">Picked Up</option>
          <option value="inspected">Inspected</option>
          <option value="refund_initiated">Refund Initiated</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : returns.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <RotateCcw className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No return requests</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Return #</th>
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Buyer</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium text-right">Refund</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {returns.map(ret => (
                <tr key={ret.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{ret.return_number}</td>
                  <td className="px-4 py-3">#{ret.order?.order_number || ret.order_id}</td>
                  <td className="px-4 py-3">{ret.buyer?.name || '—'}</td>
                  <td className="px-4 py-3 capitalize">{ret.type}</td>
                  <td className="px-4 py-3 text-xs">{ret.reason?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-right">
                    {ret.refund_amount ? `Rs.${Number(ret.refund_amount).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ret.status]}`}>
                      {ret.status?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {ret.status === 'requested' && (
                      <>
                        <button
                          onClick={() => approveMut.mutate(ret.id)}
                          className="text-green-600 hover:text-green-800 text-xs font-medium"
                        >
                          <Check className="inline w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Rejection reason:')
                            if (reason) rejectMut.mutate({ id: ret.id, reason })
                          }}
                          className="text-red-600 hover:text-red-800 text-xs font-medium"
                        >
                          <X className="inline w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                    {ret.status === 'approved' && (
                      <button
                        onClick={() => transitionMut.mutate({ id: ret.id, status: 'pickup_scheduled' })}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        <Truck className="inline w-3.5 h-3.5" /> Schedule Pickup
                      </button>
                    )}
                    {ret.status === 'picked_up' && (
                      <button
                        onClick={() => transitionMut.mutate({ id: ret.id, status: 'inspected' })}
                        className="text-blue-600 text-xs font-medium"
                      >
                        Mark Inspected
                      </button>
                    )}
                    {ret.status === 'inspected' && (
                      <button
                        onClick={() => transitionMut.mutate({ id: ret.id, status: 'refund_initiated' })}
                        className="text-green-600 text-xs font-medium"
                      >
                        Initiate Refund
                      </button>
                    )}
                    {ret.status === 'refund_initiated' && (
                      <button
                        onClick={() => transitionMut.mutate({ id: ret.id, status: 'completed' })}
                        className="text-green-600 text-xs font-medium"
                      >
                        Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
